// ── ListBandHeader ────────────────────────────────────────────────────────────
// La banda separadora de una lista. UN solo componente para todos los módulos:
// antes cada lista escribía la suya y quedaban distintas entre sí — unas con
// chevron y otras sin, unas con contador y otras sin, y tamaños de letra y
// espaciados que no coincidían. La forma canónica es la de las listas con
// bandas temporales (correo, reportes, facturación), que es la más completa.
//
// Anatomía fija: [casilla] [chevron] ETIQUETA (n) ......... [extra]
// - La etiqueta va SIEMPRE en MAYÚSCULAS — lo pone el contenedor, nada de
//   adentro lo pisa.
// - El contador va SIEMPRE si hay algo que contar: saber cuántos hay sin
//   desplegar es la mitad de para qué sirve la banda.
// - El chevron existe solo si la banda se puede plegar (onToggle presente).
// - La casilla existe solo si la banda selecciona en bloque.

import type { ReactNode } from 'react';
import { Icon } from '../Icon.js';
import { Checkbox } from './Checkbox.js';
import { Pressable } from './Pressable.js';

export interface ListBandHeaderProps {
  label: string;
  /** Cuántos elementos hay en la banda. Se omite solo si no aplica contar. */
  count?: number;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Selección en bloque. Sin estos tres, la banda no lleva casilla. */
  checked?: boolean;
  indeterminate?: boolean;
  onCheckChange?: () => void;
  /** Contenido al final de la fila (contador de sin leer, acciones). */
  right?: ReactNode;
  /** Sin bandas de fondo ni bordes: para listas que ya viven dentro de una
   *  tarjeta y no necesitan la franja (el acordeón de tareas, por ejemplo). */
  plain?: boolean;
}

export function ListBandHeader({
  label, count, collapsed = false, onToggle,
  checked, indeterminate, onCheckChange, right, plain = false,
}: ListBandHeaderProps) {
  const contenido = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      {onToggle && (
        <span style={{
          display: 'inline-flex',
          transform: collapsed ? 'rotate(-90deg)' : 'none',
          transition: 'transform 0.15s',
          flexShrink: 0,
        }}>
          <Icon name="chevron-down" size={10} strokeWidth={2} />
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {count !== undefined && (
        <span style={{ fontWeight: 400, textTransform: 'none' }}>({count})</span>
      )}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // La sangría del modo plain sale de --evo-band-inset, que define quien
        // monta la lista para que coincida con SU fila. El valor por defecto es
        // el del log de Mensajería (la sangría del log + el padding de la fila).
        //
        // Antes estaba fijo a ese cálculo, así que en una lista de SubRow —10px
        // de padding— el cotejo de la banda quedaba 30px a la derecha del de
        // las filas.
        padding: plain
          ? 'var(--evo-space-3) var(--evo-band-inset, calc(var(--evo-log-inset) + var(--evo-log-row-px))) var(--evo-space-1)'
          : '6px var(--evo-list-pad-x)',
        fontSize: 'var(--evo-fs-label)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 'var(--evo-ls-caps)',
        background: plain ? 'transparent' : 'var(--evo-bg-2)',
        borderTop: plain ? 'none' : '1px solid var(--evo-stroke)',
        borderBottom: plain ? 'none' : '1px solid var(--evo-stroke)',
        color: 'var(--evo-fg-3)',
        userSelect: 'none',
      }}
    >
      {onCheckChange && (
        // Checkbox ya corta su propia propagación — un solo wrapper visual, un
        // solo disparo real.
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <Checkbox
            checked={checked ?? false}
            {...(indeterminate !== undefined ? { indeterminate } : {})}
            onChange={onCheckChange}
          />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {onToggle
          ? <Pressable fullWidth onPress={onToggle}>{contenido}</Pressable>
          : contenido}
      </div>
      {right}
    </div>
  );
}
